/**
 * P2.5: MfaSetupPage
 *
 * Guides the authenticated doctor through TOTP MFA enrollment.
 * Route: /mfa/setup
 *
 * Flow:
 *   1. POST /api/auth/mfa/enroll → get { secret, qrUri, backupCodes }
 *   2. Show QR code (as <img> from qrUri) + backup codes
 *   3. User enters first 6-digit code from authenticator
 *   4. POST /api/auth/mfa/verify-enroll → confirm enrollment
 *   5. Redirect to /dashboard with success toast
 */

import { useState, useEffect } from 'react';
import QRCode from 'qrcode';

function QrCode({ uri, size = 200 }: { uri: string; size?: number }) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    setDataUrl(null);
    QRCode.toDataURL(uri, { width: size, margin: 1, errorCorrectionLevel: 'M' })
      .then((url) => { if (!cancelled) setDataUrl(url); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [uri, size]);
  if (failed) {
    return (
      <div
        style={{ width: size, height: size }}
        className="rounded-lg border border-clinical-cream-dark bg-gray-50 flex items-center justify-center text-xs text-gray-500 px-2 text-center"
      >
        QR unavailable — use the manual code below
      </div>
    );
  }
  if (!dataUrl) {
    return (
      <div
        style={{ width: size, height: size }}
        className="rounded-lg border border-clinical-cream-dark bg-gray-50 animate-pulse"
        aria-label="Generating QR code"
      />
    );
  }
  return (
    <img
      src={dataUrl}
      alt="MFA QR code — scan with your authenticator app"
      className="rounded-lg border border-clinical-cream-dark"
      width={size}
      height={size}
    />
  );
}
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface EnrollData {
  secret: string;
  qrUri: string;
  backupCodes: string[];
}

export function MfaSetupPage() {
  const { accessToken: token } = useAuth();
  const navigate = useNavigate();

  const [enrollData, setEnrollData] = useState<EnrollData | null>(null);
  const [step, setStep] = useState<'loading' | 'scan' | 'verify' | 'done'>('loading');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copiedBackups, setCopiedBackups] = useState(false);

  const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

  useEffect(() => {
    if (!token) return;
    fetch(`${BASE}/api/auth/mfa/enroll`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      credentials: 'include',
    })
      .then((r) => r.json())
      .then((data: unknown) => {
        setEnrollData(data as EnrollData);
        setStep('scan');
      })
      .catch((err: Error) => {
        setError(err.message || 'Failed to initiate MFA enrollment');
      });
  }, [token, BASE]);

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    if (isSubmitting) return;
    setError(null);
    setIsSubmitting(true);
    try {
      const res = await fetch(`${BASE}/api/auth/mfa/verify-enroll`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code }),
      });
      if (!res.ok) throw new Error('Invalid code');
      setStep('done');
      setTimeout(() => navigate('/dashboard', { replace: true }), 2000);
    } catch {
      setError('Invalid code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  function copyBackupCodes() {
    if (!enrollData) return;
    void navigator.clipboard.writeText(enrollData.backupCodes.join('\n'));
    setCopiedBackups(true);
    setTimeout(() => setCopiedBackups(false), 2000);
  }

  if (step === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
        <div className="w-8 h-8 border-2 border-clinical-amber border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (step === 'done') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
        <div className="clinical-card p-8 text-center max-w-sm w-full">
          <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
          </div>
          <h2 className="font-heading text-xl text-clinical-charcoal mb-2">MFA Enabled</h2>
          <p className="font-body text-clinical-text-muted text-sm">Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)] px-4 py-8">
      <div className="clinical-card p-8 w-full max-w-md">
        <h1 className="font-heading text-2xl text-clinical-charcoal mb-2">Set Up Two-Factor Authentication</h1>
        <p className="font-body text-clinical-text-muted text-sm mb-6">
          Scan the QR code with Google Authenticator, Authy, or another TOTP app.
        </p>

        {enrollData && (
          <>
            {/* QR Code — rendered client-side via qrcode library (no external network) */}
            <div className="flex justify-center mb-4">
              <QrCode uri={enrollData.qrUri} size={200} />
            </div>

            {/* Manual secret */}
            <details className="mb-6">
              <summary className="font-body text-sm text-clinical-text-muted cursor-pointer mb-1">
                Can't scan? Enter manually
              </summary>
              <code className="block mt-2 text-xs font-mono bg-clinical-cream-dark p-2 rounded break-all select-all">
                {enrollData.secret}
              </code>
            </details>

            {/* Backup codes */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-heading text-sm text-clinical-charcoal">Backup Codes</h3>
                <button
                  type="button"
                  onClick={copyBackupCodes}
                  className="font-body text-xs text-clinical-amber hover:underline"
                >
                  {copiedBackups ? 'Copied!' : 'Copy all'}
                </button>
              </div>
              <p className="font-body text-xs text-clinical-text-muted mb-2">
                Save these codes somewhere safe. Each can be used once if you lose access to your authenticator.
              </p>
              <div className="grid grid-cols-2 gap-1">
                {enrollData.backupCodes.map((c) => (
                  <code key={c} className="font-mono text-xs bg-clinical-cream-dark p-1 rounded text-center">
                    {c}
                  </code>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Verify step */}
        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <label htmlFor="mfa-code" className="block font-body text-sm text-clinical-charcoal mb-1">
              Enter the 6-digit code from your app
            </label>
            <input
              id="mfa-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              className="clinical-input w-full text-center text-xl tracking-widest"
              placeholder="000000"
              required
            />
          </div>

          {error && (
            <p className="font-body text-sm text-red-600">{error}</p>
          )}

          <button
            type="submit"
            disabled={isSubmitting || code.length !== 6}
            className="clinical-button-primary w-full"
          >
            {isSubmitting ? 'Verifying...' : 'Confirm & Enable MFA'}
          </button>
        </form>
      </div>
    </div>
  );
}
